import * as Toast from "@radix-ui/react-toast";
import { UserPlus, User, Mail, Lock, Shield, ShieldOff } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface CreateUserDialogProps {
  onUserCreate: () => void;
}

export function CreateUserDialog({ onUserCreate }: CreateUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("BROKER");
  const [blocked, setBlocked] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleCreateUser() {
    const newUser = {
      username,
      email,
      password,
      role: 2,
      confirmed: true,
      blocked,
      collaborator: role,
    };

    setLoading(true);
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newUser),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error data:", errorData);
        const errorMessage =
          errorData.message && errorData.message[0].messages
            ? errorData.message[0].messages[0].message
            : "Falha ao criar o usuário";
        throw new Error(errorMessage);
      }

      setOpen(false);
      setUsername("");
      setEmail("");
      setPassword("");
      setRole("BROKER");
      setBlocked(false);
      onUserCreate();
      setToastType("success");
      setToastMessage("Usuário criado com sucesso!");
      setToastOpen(true);
    } catch (error: unknown) {
      console.error("Failed to create user:", error);
      setToastOpen(true);
      setToastType("error");
      setToastMessage(
        error instanceof Error && error.message.includes("already exists")
          ? "Usuário já existe!"
          : "Falha ao criar o usuário!",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-purple-700">
              <UserPlus className="h-5 w-5" />
              Cadastrar Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl bg-white p-6 shadow-2xl sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                <UserPlus className="h-6 w-6 text-blue-600" />
                Criar Novo Usuário
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Preencha as informações do novo usuário e clique em salvar.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label
                  htmlFor="username"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  <User className="h-4 w-4" />
                  Nome
                </Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Digite o nome do usuário"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Digite o email do usuário"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  <Lock className="h-4 w-4" />
                  Senha
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Digite a senha do usuário"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Cargo
                </Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={`h-12 flex-1 rounded-xl transition-all duration-200 ${
                      role === "ADMIN"
                        ? "bg-blue-600 text-white shadow-lg hover:bg-blue-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setRole("ADMIN")}
                  >
                    Administrador
                  </Button>
                  <Button
                    type="button"
                    className={`h-12 flex-1 rounded-xl transition-all duration-200 ${
                      role === "BROKER"
                        ? "bg-green-600 text-white shadow-lg hover:bg-green-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setRole("BROKER")}
                  >
                    Corretor
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Status Inicial
                </Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl transition-all duration-200 ${
                      !blocked
                        ? "bg-green-600 text-white shadow-lg hover:bg-green-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBlocked(false)}
                  >
                    <Shield className="h-4 w-4" />
                    Acesso Liberado
                  </Button>
                  <Button
                    type="button"
                    className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl transition-all duration-200 ${
                      blocked
                        ? "bg-red-600 text-white shadow-lg hover:bg-red-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBlocked(true)}
                  >
                    <ShieldOff className="h-4 w-4" />
                    Acesso Bloqueado
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                className="h-12 rounded-xl bg-gray-100 px-6 text-gray-700 hover:bg-gray-200"
                onClick={() => setOpen(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleCreateUser}
                disabled={loading}
                className="h-12 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 text-white hover:from-blue-700 hover:to-purple-700"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Criando...
                  </div>
                ) : (
                  "Criar Usuário"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={5000}
          className={`
            fixed bottom-4 right-4 rounded-xl border p-4 shadow-lg backdrop-blur-sm
            ${
              toastType === "success"
                ? "border-green-400 bg-green-500/90 text-white"
                : "border-red-400 bg-red-500/90 text-white"
            }
          `}
        >
          <Toast.Description className="font-medium">
            {toastMessage}
          </Toast.Description>
          <Toast.Close className="absolute right-2 top-2 text-white/80 hover:text-white" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
